import { BifrostWeb, bifrostImageToDataUrl } from "@seekrtech/bifrost-web";
import { breadcrumb } from "@forest/native-diagnostics";
import { compare } from "compare-versions";
import { FailureCauseError, fetchExperimentProducts, getSkuForPlatform, inferProductType, mapPlatformToServiceApi, nativeErrorCause } from "@forest/paywall-core";
import pRetry from "p-retry";
//#region src/deferred.ts
function createDeferred() {
	let resolve;
	return {
		promise: new Promise((r) => {
			resolve = r;
		}),
		done: resolve
	};
}
//#endregion
//#region src/services/assets.ts
function createAssetsService(bifrost) {
	return bifrost.createNativeProxy("AssetsService");
}
//#endregion
//#region src/breadcrumb.ts
/**
* Short label for a failed bridge step on the boot breadcrumb line: the error
* class, or its message when the class is a bare `Error` (bifrost's
* `RPC call timeout`). The line is capped, so this stays one short token.
*/
function describeFailure(error) {
	if (!(error instanceof Error)) return typeof error;
	return (error.name === "Error" ? error.message : error.name).replace(/\s+/g, "_");
}
//#endregion
//#region src/page-visible-internal.ts
/**
* How long a startup operation waits for the native presentation to announce
* itself before proceeding without it.
*
* `pageVisible` is delivered as soon as the WebView becomes visible — well
* under a second on both platforms — so this bound is never reached by a real
* presentation. It exists because there may be no presentation at all (a page
* opened outside the native cover, or a delivery that is lost), and an
* unbounded wait there is a deadlock, not a wait.
*/
const PAGE_VISIBLE_WAIT_MS = 2e3;
let current = createDeferred();
const pageVisibleOnce = {
	get promise() {
		return current.promise;
	},
	done: (v) => current.done(v),
	/**
	* Wait for the presentation announcement, but never forever.
	*
	* Resolves when `pageVisible` arrives, or when the bound elapses, and never
	* rejects: an operation that waited in vain simply proceeds with no page
	* budget, which is exactly how it behaved before recovery existed. Callers
	* that must not hang on a presentation that never speaks use this instead of
	* awaiting `promise` directly.
	*/
	waitAtMost: (timeoutMs = PAGE_VISIBLE_WAIT_MS) => {
		const announced = current.promise;
		return new Promise((resolve) => {
			const timer = setTimeout(resolve, timeoutMs);
			announced.then(() => {
				clearTimeout(timer);
				resolve();
			});
		});
	},
	/** @internal — used by __resetForTests in native.ts */
	__resetForTests() {
		current = createDeferred();
	}
};
//#endregion
//#region src/page-loading-recovery-internal.ts
/**
* Ceiling for one attempt at a repeatable native read.
*
* Inside an armed presentation it caps the attempt so a lost reply still leaves
* room for another read before the deadline. Outside one it is the plain bound
* a read is given so that it settles at all: there is no budget to retry in,
* but a read that never settles is not a terminal outcome either.
*/
const MAX_NATIVE_READ_ATTEMPT_MS = 3e3;
const PAGE_LOADING_RETRY_DELAY_MS = 100;
const NEVER_REPLAY = Symbol.for("forest.page-loading.never-replay");
let deadline;
let phase = "idle";
let generation = 0;
let cancellation = new AbortController();
let lifetime = new AbortController();
function beginPageLoadingRecovery(payload) {
	if (phase !== "idle") return;
	const remaining = payload?.remaining_timeout_ms;
	if (typeof remaining !== "number" || !Number.isFinite(remaining) || remaining < 0) return;
	deadline = performance.now() + remaining;
	phase = "active";
}
function markPageLoadingReady() {
	if (phase !== "active") return;
	phase = "ready";
	generation += 1;
	cancellation.abort();
}
function cancelPageLoadingRecovery() {
	if (phase === "cancelled") return;
	phase = "cancelled";
	generation += 1;
	cancellation.abort();
	lifetime.abort();
}
/**
* Begin a bounded attempt at a verified repeatable native read (user info,
* notification permission status). Returns `undefined` when this read is not
* inside a live page-loading attempt, so the caller keeps its previous call
* shape: when no native presentation armed a budget, when reveal has ended a
* reveal-gated scope, and — the case `survivesReveal` reaches — when the
* original budget is spent.
*
* That last case matters beyond the pages this spec covers.
* `survivesReveal` keeps a scope recovering in phase `ready`, and only leaving
* the page ends it, so without this guard a read minutes past the deadline
* still got an attempt whose timeout was `floor(remaining / 2)` — zero. Bifrost
* honours an explicit zero as a real deadline, so the call was dispatched and
* abandoned on the next macrotask and the read failed with `Page loading has
* ended`. The shared notification-status read is called after a purchase by
* nine paywalls that swallow its rejection, so every user who browsed longer
* than the cover budget silently lost the grant-permission dialog. An attempt
* that cannot fit is not an attempt (spec Decision 4); the read belongs on the
* pre-existing path instead.
*/
function beginNativeReadAttempt(maxAttemptMs = MAX_NATIVE_READ_ATTEMPT_MS, scope = {}) {
	if (phase === "cancelled") throw pageLoadingEndedError();
	if (!isRecovering(scope) || deadline === void 0) return void 0;
	const remaining = Math.max(0, deadline - performance.now());
	if (remaining <= 200) return void 0;
	return {
		generation,
		timeout: Math.min(maxAttemptMs, Math.floor(remaining / 2))
	};
}
function assertPageLoadingAttemptIsActive(attempt, scope = {}) {
	if (deadline === void 0 || performance.now() >= deadline) throw pageLoadingEndedError();
	if (!isRecovering(scope)) throw pageLoadingEndedError();
	if (!scope.survivesReveal && generation !== attempt.generation) throw pageLoadingEndedError();
}
function nativeReadRetryInstruction(attempt, cause, scope = {}) {
	assertPageLoadingAttemptIsActive(attempt, scope);
	return pageLoadingRetryInstruction(cause, scope);
}
/** Whether the given scope is still recovering in the current phase. */
function isRecovering(scope) {
	if (phase === "active") return true;
	return phase === "ready" && scope.survivesReveal === true;
}
/**
* Mark a failure that page-loading recovery must never replay: the operation
* may already have taken effect (a permission prompt, a purchase), or its own
* attempt budget is spent. The caller's own error still reaches its existing
* terminal handling.
*/
function markNeverReplay(error) {
	if (typeof error === "object" && error !== null) Object.defineProperty(error, NEVER_REPLAY, { value: true });
	return error;
}
function isNeverReplay(error) {
	return typeof error === "object" && error !== null && error[NEVER_REPLAY] === true;
}
/**
* Whether the page-loading budget still allows another attempt at a
* prerequisite. An unarmed presentation (no native budget) reports `true` so an
* existing retry policy keeps its own behaviour untouched. Reveal does not end
* the budget: a page whose product work is still pending after its content
* appears keeps the original deadline. Leaving the page does end it.
*/
function hasPageLoadingBudget() {
	if (phase === "cancelled") return false;
	if (deadline === void 0) return true;
	return performance.now() < deadline;
}
/**
* Whether the page has been left. Distinct from the budget: an expired deadline
* still belongs to a present page, so its operation may run its own terminal
* behaviour, while a page that is already gone must not produce another
* terminal callback or mutate its successor.
*/
function hasPageLoadingEnded() {
	return phase === "cancelled";
}
/**
* Retry a verified repeatable prerequisite read inside the original page
* budget. A rejection is retried only while the attempt is still the live one
* and the budget can fit another try; otherwise the caller sees its own error
* and the page's existing terminal policy applies.
*/
async function withPageLoadingRecovery(run, scope = {}) {
	while (true) try {
		return await run();
	} catch (error) {
		if (isNeverReplay(error)) throw error;
		const instruction = isRetryInstruction(error) ? error : pageLoadingRetryInstruction(error, scope);
		if (!instruction) throw error;
		await instruction.waitForPageLoadingRetry();
	}
}
function isRetryInstruction(error) {
	return typeof error === "object" && error !== null && "waitForPageLoadingRetry" in error && typeof error.waitForPageLoadingRetry === "function";
}
function pageLoadingRetryInstruction(cause, scope = {}) {
	if (!isRecovering(scope) || deadline === void 0) return void 0;
	if (deadline - performance.now() <= 200) return void 0;
	const error = new Error("Retry page loading prerequisite", { cause });
	error.waitForPageLoadingRetry = () => new Promise((resolve, reject) => {
		const signal = scope.survivesReveal ? lifetime.signal : cancellation.signal;
		if (signal.aborted) {
			reject(pageLoadingEndedError());
			return;
		}
		const onAbort = () => {
			clearTimeout(timer);
			reject(pageLoadingEndedError());
		};
		const timer = setTimeout(() => {
			signal.removeEventListener("abort", onAbort);
			resolve();
		}, PAGE_LOADING_RETRY_DELAY_MS);
		signal.addEventListener("abort", onAbort, { once: true });
	});
	return error;
}
function pageLoadingEndedError() {
	return /* @__PURE__ */ new Error("Page loading has ended");
}
//#endregion
//#region src/native.ts
let proxies = null;
let appVersion = "0.0.0";
/** Native version that shipped ExperimentService and NotificationService. */
const EXPERIMENT_NOTIFICATION_MIN_VERSION = "5.27.0";
/** Native version whose UserService hands web the service level status. */
const SERVICE_LEVEL_MIN_VERSION = "5.27.0";
/**
* Whether the host app is at least `min`. A version string the host sends
* that is not a version at all (e.g. an empty `app_version` param) counts as
* below every gate rather than throwing out of a `native.*` call.
*/
function appVersionAtLeast(min) {
	try {
		return compare(appVersion, min, ">=");
	} catch {
		return false;
	}
}
/** @internal — set by initializeNativeBridge. Throws if called twice. */
function __setProxies(next) {
	if (proxies) throw new Error("native services already initialized");
	proxies = next;
}
/** @internal — set by initializeNativeBridge. */
function __setAppVersion(next) {
	appVersion = next;
}
function requireProxy(key) {
	if (!proxies) throw new Error("native services not initialized — call initializeNativeBridge() first");
	return proxies[key];
}
const native = {
	auth: {
		auth: () => requireProxy("auth").auth(),
		bindLegacyUser: () => requireProxy("auth").bindLegacyUser()
	},
	user: {
		getUserInfo: () => requireProxy("user").getUserInfo(),
		refreshToken: () => requireProxy("user").refreshToken(),
		getServiceLevel: async () => {
			if (!appVersionAtLeast(SERVICE_LEVEL_MIN_VERSION)) return {
				success: false,
				error: "unsupported"
			};
			return requireProxy("user").getServiceLevel();
		},
		refreshServiceLevel: () => requireProxy("user").refreshServiceLevel().then((result) => result && appVersionAtLeast(SERVICE_LEVEL_MIN_VERSION) ? result : {
			success: false,
			error: "unsupported"
		})
	},
	permission: {
		getHasNotificationPermission: () => requireProxy("permission").getHasNotificationPermission(),
		requestNotificationPermission: () => requireProxy("permission").requestNotificationPermission()
	},
	general: {
		close: () => {
			cancelPageLoadingRecovery();
			return requireProxy("general").close();
		},
		logEvent: (p) => {
			console.log("[native-services] logEvent", p.event_name, JSON.stringify(p.event_params ?? {}));
			return requireProxy("general").logEvent(p);
		},
		openURL: (url) => requireProxy("general").openURL(url),
		pageReady: () => {
			if (hasPageLoadingEnded()) {
				breadcrumb("pr-");
				return Promise.resolve();
			}
			markPageLoadingReady();
			breadcrumb("pr");
			const sent = requireProxy("general").pageReady();
			Promise.resolve(sent).then(() => breadcrumb("pr+"), (error) => breadcrumb("pr!", describeFailure(error)));
			return sent;
		},
		complete: () => {
			cancelPageLoadingRecovery();
			return requireProxy("general").complete();
		},
		getPageContext: () => requireProxy("general").getPageContext(),
		logFatalError: (p) => requireProxy("general").logFatalError(p),
		logNonFatalError: (p) => requireProxy("general").logNonFatalError(p)
	},
	data: { fullSync: () => requireProxy("data").fullSync() },
	assets: { getImageByName: (name) => requireProxy("assets").getImageByName(name) },
	paywall: { openPaywall: (...args) => requireProxy("paywall").openPaywall(...args) },
	iap: {
		getPrice: (p) => requireProxy("iap").getPrice(p),
		purchase: (p) => requireProxy("iap").purchase(p),
		closeTransaction: (p) => requireProxy("iap").closeTransaction(p),
		getUnclosedTransaction: (p) => requireProxy("iap").getUnclosedTransaction(p),
		getAllUnclosedTransactions: () => requireProxy("iap").getAllUnclosedTransactions(),
		getAllEntitlements: () => requireProxy("iap").getAllEntitlements(),
		getAvailableSubscriptionProducts: () => requireProxy("iap").getAvailableSubscriptionProducts(),
		getStoreRegion: () => requireProxy("iap").getStoreRegion(),
		getReceiptSystemAccountId: () => requireProxy("iap").getReceiptSystemAccountId()
	},
	experiment: { getGroup: async (p) => {
		if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) return {
			success: false,
			error: "not_registered"
		};
		return requireProxy("experiment").getGroup(p);
	} },
	notification: {
		schedule: async (p) => {
			if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) return {
				success: false,
				error: "unsupported"
			};
			return requireProxy("notification").schedule(p);
		},
		getScheduled: async (p) => {
			if (!appVersionAtLeast(EXPERIMENT_NOTIFICATION_MIN_VERSION)) return {
				success: false,
				error: "unsupported"
			};
			return requireProxy("notification").getScheduled(p);
		}
	},
	additional: { getInitialThemeId: () => requireProxy("additional").getInitialThemeId() },
	l10n: { getLocalizedString: async (p) => {
		if (!appVersionAtLeast("5.17.0")) return p.key;
		const result = await requireProxy("l10n").getLocalizedString(p);
		if (!result.success) return p.key;
		return result.data ?? p.key;
	} }
};
//#endregion
//#region src/page-visible-handlers.ts
function defaultSetSafeAreaInsets(payload) {
	Object.entries(payload).forEach(([key, value]) => {
		document.documentElement.style.setProperty(`--safe-area-${key}`, `${Math.round(value)}px`);
	});
}
//#endregion
//#region src/services/permission.ts
/**
* The status read is not any page's reveal gate — the walkthrough reveals on
* its own animation — so revealing must not end its recovery. It stays bounded
* by the presentation's original deadline (spec Decision 10).
*/
const STATUS_READ_SCOPE = { survivesReveal: true };
function createPermissionService(bifrost) {
	const proxy = bifrost.createProxyBuilder("PermissionService").withDefaultOptions({ timeout: 31536e6 }).build();
	return {
		getHasNotificationPermission: async () => {
			const attempt = beginNativeReadAttempt(void 0, STATUS_READ_SCOPE);
			if (!attempt) return proxy.getHasNotificationPermission({ timeout: MAX_NATIVE_READ_ATTEMPT_MS });
			try {
				return await proxy.getHasNotificationPermission({ timeout: attempt.timeout });
			} catch (error) {
				throw nativeReadRetryInstruction(attempt, error, STATUS_READ_SCOPE) ?? error;
			}
		},
		requestNotificationPermission: async () => {
			try {
				return await proxy.requestNotificationPermission();
			} catch (error) {
				throw markNeverReplay(error);
			}
		}
	};
}
//#endregion
//#region src/services/auth.ts
function createAuthService(bifrost) {
	return bifrost.createProxyBuilder("AuthService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/user.ts
function createUserService(bifrost) {
	const proxy = bifrost.createProxyBuilder("UserService").withDefaultOptions({ timeout: 3e4 }).build();
	return {
		getUserInfo: async () => {
			const attempt = beginNativeReadAttempt();
			if (!attempt) return proxy.getUserInfo();
			try {
				return await proxy.getUserInfo({ timeout: attempt.timeout });
			} catch (error) {
				throw nativeReadRetryInstruction(attempt, error) ?? error;
			}
		},
		refreshToken: () => proxy.refreshToken(),
		getServiceLevel: () => proxy.getServiceLevel(),
		refreshServiceLevel: () => proxy.refreshServiceLevel()
	};
}
//#endregion
//#region src/services/general.ts
function createGeneralService(bifrost) {
	return bifrost.createNativeProxy("GeneralService");
}
//#endregion
//#region src/services/iap.ts
function createIAPService(bifrost) {
	return bifrost.createProxyBuilder("IAPService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/additional.ts
function createAdditionalService(bifrost) {
	return bifrost.createNativeProxy("RelaxPaywallService");
}
//#endregion
//#region src/services/l10n.ts
function createL10nService(bifrost) {
	return bifrost.createNativeProxy("L10nService");
}
//#endregion
//#region src/services/data.ts
function createDataService(bifrost) {
	return bifrost.createNativeProxy("DataService");
}
//#endregion
//#region src/services/paywall.ts
function createPaywallService(bifrost) {
	return bifrost.createProxyBuilder("PaywallService").withDefaultOptions({ timeout: 31536e6 }).build();
}
//#endregion
//#region src/services/experiment.ts
function createExperimentService(bifrost) {
	return bifrost.createNativeProxy("ExperimentService");
}
//#endregion
//#region src/services/notification.ts
function createNotificationService(bifrost) {
	return bifrost.createNativeProxy("NotificationService");
}
//#endregion
//#region src/bifrost.ts
/** Must be called exactly once before any `native.*` call. Throws if called twice. */
function initializeNativeBridge(options = {}) {
	const bifrost = options._bifrost ?? new BifrostWeb(options.bifrost ?? {});
	__setAppVersion(options.appVersion ?? "0.0.0");
	__setProxies({
		auth: createAuthService(bifrost),
		user: createUserService(bifrost),
		permission: createPermissionService(bifrost),
		general: createGeneralService(bifrost),
		iap: createIAPService(bifrost),
		data: createDataService(bifrost),
		assets: createAssetsService(bifrost),
		paywall: createPaywallService(bifrost),
		experiment: createExperimentService(bifrost),
		notification: createNotificationService(bifrost),
		additional: createAdditionalService(bifrost),
		l10n: createL10nService(bifrost)
	});
	const setSafeAreaInsets = options.onSetSafeAreaInsets ?? defaultSetSafeAreaInsets;
	bifrost.registerService("GeneralService", {
		pageVisible: (payload) => {
			breadcrumb("pv", payload?.remaining_timeout_ms ?? void 0);
			beginPageLoadingRecovery(payload);
			options.onPageVisible?.();
			pageVisibleOnce.done();
		},
		setSafeAreaInsets
	});
	const ready = bifrost.initialize();
	Promise.resolve(ready).then(() => breadcrumb("hs"), (error) => breadcrumb("hs!", describeFailure(error)));
	return {
		bifrost,
		ready
	};
}
//#endregion
//#region src/products/monthly-no-trial.ts
const MONTHLY_NO_TRIAL = {
	production: {
		ios: {
			product_id: 64,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 64,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	},
	staging: {
		ios: {
			product_id: 68,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 68,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	},
	dev: {
		ios: {
			product_id: 57,
			sku: "forest_monthly_early_bird_2"
		},
		googleplay_android: {
			product_id: 57,
			sku: "forest_plus-monthly-early-bird-2"
		},
		china_android: null
	}
};
function buildMonthlyNoTrial(config, subscriptionProducts) {
	const nativeProduct = subscriptionProducts.find((p) => p.sku === config.sku);
	return {
		product_id: config.product_id,
		receipt_system_product_id: 0,
		sku: config.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: 0,
		offers: nativeProduct?.offers,
		price_localized: nativeProduct?.price_localized ?? "",
		price_value: nativeProduct?.price_value ?? 0,
		display_name: nativeProduct?.display_name ?? ""
	};
}
//#endregion
//#region src/products/canonical-mock.ts
function wait(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}
const annualOriginal = {
	product_id: 21,
	receipt_system_product_id: 0,
	sku: "forest_annual_1",
	product_type: "subscription",
	duration: "yearly",
	eligible_trial_days: 0,
	price_localized: "$200.00",
	price_value: 200,
	display_name: "Annual Plan"
};
const annualEarlyBird = {
	product_id: 22,
	receipt_system_product_id: 0,
	sku: "forest_annual_early_bird",
	product_type: "subscription",
	duration: "yearly",
	eligible_trial_days: 7,
	price_localized: "$100.00",
	price_value: 100,
	display_name: "Annual Plan (Early Bird)"
};
const monthlyOriginal = {
	product_id: 23,
	receipt_system_product_id: 0,
	sku: "forest_monthly_1",
	product_type: "subscription",
	duration: "monthly",
	eligible_trial_days: 0,
	price_localized: "$20.00",
	price_value: 20,
	display_name: "Monthly Plan"
};
const monthlyEarlyBird = {
	product_id: 24,
	receipt_system_product_id: 0,
	sku: "forest_monthly_early_bird",
	product_type: "subscription",
	duration: "monthly",
	eligible_trial_days: 7,
	price_localized: "$10.00",
	price_value: 10,
	display_name: "Monthly Plan (Early Bird)"
};
async function canonicalMock(env, platform) {
	await wait(1e3);
	const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null;
	const monthlyNoTrial = noTrialConfig ? {
		product_id: noTrialConfig.product_id,
		receipt_system_product_id: 0,
		sku: noTrialConfig.sku,
		product_type: "subscription",
		duration: "monthly",
		eligible_trial_days: 0,
		price_localized: "$10.00",
		price_value: 10,
		display_name: "Monthly Plan (No Trial)"
	} : null;
	return {
		annualOriginal,
		annualEarlyBird,
		monthlyOriginal,
		monthlyEarlyBird,
		monthlyNoTrial
	};
}
//#endregion
//#region src/products/cache.ts
const TTL_MS = 36e5;
function readCached(key) {
	try {
		const raw = localStorage.getItem(key);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (Date.now() - parsed.timestamp > TTL_MS) {
			localStorage.removeItem(key);
			return null;
		}
		return parsed.data;
	} catch (error) {
		console.error("Error reading cache from localStorage:", error);
		localStorage.removeItem(key);
		return null;
	}
}
function writeCached(key, data) {
	const envelope = {
		data,
		timestamp: Date.now()
	};
	localStorage.setItem(key, JSON.stringify(envelope));
}
function readSubscriptionProductsCache(key) {
	return readCached(key);
}
function writeSubscriptionProductsCache(key, value) {
	writeCached(key, value);
}
function readExpSkusCache(key) {
	return readCached(key);
}
function writeExpSkusCache(key, value) {
	writeCached(key, value);
}
//#endregion
//#region src/products/merge.ts
/**
* Fallback used on app versions below 5.2.0 that do not support
* `getAvailableSubscriptionProducts`. Synthesizes `SubscriptionProduct[]` by
* calling `getPrice` once per SKU so downstream merging is uniform.
*/
async function getSubscriptionProductsBySkus(expSkus, platform, iap) {
	return Promise.all(expSkus.map(async (apiProduct) => {
		const sku = getSkuForPlatform(apiProduct, platform);
		const priceResult = await iap.getPrice({
			product_id: 0,
			product_type: "subscription",
			sku
		});
		if (!priceResult.success) throw new FailureCauseError(nativeErrorCause(priceResult.error), priceResult.error);
		const { isAnnual } = inferProductType(apiProduct.title);
		return {
			sku,
			duration: isAnnual ? "yearly" : "monthly",
			eligible_trial_days: 0,
			price_localized: priceResult.data.price_localized,
			price_value: priceResult.data.price_value,
			display_name: ""
		};
	}));
}
function mergeProducts(expSkus, subscriptionProducts, platform) {
	const nativeBySku = new Map(subscriptionProducts.map((p) => [p.sku, p]));
	const collected = {};
	for (const apiProduct of expSkus) {
		const sku = getSkuForPlatform(apiProduct, platform);
		const native = nativeBySku.get(sku);
		if (!native) throw new FailureCauseError("sku_missing", `Product not found for SKU: ${sku}`);
		const { isAnnual, isEarlyBird } = inferProductType(apiProduct.title);
		const built = {
			product_id: apiProduct.id,
			receipt_system_product_id: 0,
			sku,
			product_type: "subscription",
			duration: native.duration,
			eligible_trial_days: native.eligible_trial_days,
			offers: native.offers,
			price_localized: native.price_localized,
			price_value: native.price_value,
			display_name: native.display_name
		};
		if (isAnnual && isEarlyBird) collected.annualEarlyBird = built;
		else if (isAnnual) collected.annualOriginal = built;
		else if (isEarlyBird) collected.monthlyEarlyBird = built;
		else collected.monthlyOriginal = built;
	}
	if (!collected.annualOriginal || !collected.annualEarlyBird || !collected.monthlyOriginal || !collected.monthlyEarlyBird) throw new FailureCauseError("plan_missing", "Missing one of annual/monthly original/early-bird products");
	return collected;
}
//#endregion
//#region src/products/get-products.ts
/**
* Coordinate the existing product retry with the presentation's original page
* budget instead of adding another retry layer: once the budget is spent (or
* the page has ended) another attempt cannot land in time.
*
* The budget is module-global, so whether it applies has to be decided per
* call, when the call begins. A read that begins inside a live page-loading
* attempt is bounded by that attempt. A read that begins outside one — no
* native presentation at all, or a page whose cover is already gone — keeps
* p-retry's own policy, because there is no deadline for it to miss.
*/
function withinPageLoadingBudget() {
	const boundedByAttempt = hasPageLoadingBudget();
	return { shouldRetry: () => !boundedByAttempt || hasPageLoadingBudget() };
}
async function getProducts(options) {
	try {
		return await runGetProducts(options);
	} catch (error) {
		throw markNeverReplay(error);
	}
}
async function runGetProducts(options) {
	const { cacheKeyPrefix, api, appVersion, platform, deferredUserInfo, env, shouldMock } = options;
	if (shouldMock) return canonicalMock(env, platform);
	const supportsGetStoreRegion = compare(appVersion, "5.7.0", ">=");
	const supportsGetAvailableSubs = compare(appVersion, "5.2.0", ">=");
	const subsCacheKey = `${cacheKeyPrefix}.subs.${env}.${platform}`;
	const cachedSubs = supportsGetAvailableSubs ? readSubscriptionProductsCache(subsCacheKey) : null;
	const promiseSubscriptionProducts = cachedSubs ? Promise.resolve(cachedSubs) : supportsGetAvailableSubs ? pRetry(async () => {
		const r = await native.iap.getAvailableSubscriptionProducts();
		if (!r.success) throw new FailureCauseError(nativeErrorCause(r.error), r.error);
		return r.data.products;
	}, withinPageLoadingBudget()) : null;
	const promiseStoreRegion = supportsGetStoreRegion ? native.iap.getStoreRegion().then((r) => r.success ? r.data.region : null).catch(() => null) : Promise.resolve(null);
	const userInfo = await deferredUserInfo;
	const expCacheKey = `${cacheKeyPrefix}.exp.${userInfo.user_id}`;
	const cachedExp = readExpSkusCache(expCacheKey);
	const promiseExpSkus = cachedExp ? Promise.resolve(cachedExp) : promiseStoreRegion.then((storeRegion) => fetchExperimentProducts(api.service, {
		userId: userInfo.user_id,
		platform: mapPlatformToServiceApi(platform),
		storeRegion
	}));
	const finalSubscriptionProducts = promiseSubscriptionProducts ?? pRetry(async () => {
		return getSubscriptionProductsBySkus(await promiseExpSkus, platform, native.iap);
	}, withinPageLoadingBudget());
	const [expSkus, subscriptionProducts] = await Promise.all([promiseExpSkus, finalSubscriptionProducts]);
	if (!cachedSubs && supportsGetAvailableSubs) writeSubscriptionProductsCache(subsCacheKey, subscriptionProducts);
	if (!cachedExp) writeExpSkusCache(expCacheKey, expSkus);
	const four = mergeProducts(expSkus, subscriptionProducts, platform);
	const noTrialConfig = MONTHLY_NO_TRIAL[env]?.[platform] ?? null;
	const monthlyNoTrial = noTrialConfig ? buildMonthlyNoTrial(noTrialConfig, subscriptionProducts) : null;
	return {
		...four,
		monthlyNoTrial
	};
}
//#endregion
//#region src/products/receipt-system.ts
async function getReceiptSystemAccountIdSafe(appVersion) {
	if (!compare(appVersion, "5.10.0", ">=")) return null;
	try {
		return await pRetry(async () => {
			const result = await native.iap.getReceiptSystemAccountId();
			if (!result.success) throw new Error(result.error);
			return result.data.receipt_system_account_id;
		}, { retries: 2 });
	} catch (error) {
		native.general.logNonFatalError({
			name: "failed_to_get_receipt_system_account_id",
			code: 1,
			params: {
				message: String(error),
				app_version: appVersion
			}
		}).catch(() => {});
		return null;
	}
}
//#endregion
//#region src/products/store-region.ts
async function getStoreRegionSafe(appVersion) {
	if (!compare(appVersion, "5.7.0", ">=")) return null;
	const result = await native.iap.getStoreRegion();
	if (!result.success) return null;
	return result.data.region;
}
//#endregion
export { bifrostImageToDataUrl, createDeferred, getProducts, getReceiptSystemAccountIdSafe, getStoreRegionSafe, hasPageLoadingEnded, initializeNativeBridge, markNeverReplay, native, STATUS_READ_SCOPE as notificationPermissionStatusScope, pageVisibleOnce, withPageLoadingRecovery };

//# sourceMappingURL=index.js.map